// src/pages/Index.tsx
// Updated: 2026-08-21 10:40:00 CDT
// Purpose: Open returning users in the editor and provide persistent public playback settings and direct navigation.

import { Activity, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from 'react-dom';
import { blockSettings, type BlockSettings } from '../../supabase/functions/_shared/block-settings';
import type { User } from "@supabase/supabase-js";
import Header, { type PublicPage } from "@/components/Header";
import FileUpload, { type ProcessedScreenplay } from "@/components/FileUpload";
import CharacterVoiceAssignment from "@/components/CharacterVoiceAssignment";
import ScreenplayEditor from "@/components/ScreenplayEditor";
import ScreenplayHistory, {
  type ScreenplayHistoryItem,
} from "@/components/ScreenplayHistory";
import PublicSettings from "@/pages/PublicSettings";
import Auth from "@/components/Auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { estimatedScreenplayPages, withinConversionPages } from '@/utils/screenplayPages';
import { groupCachedAudioBlocks } from '@/utils/cachedAudioBlocks';
import { screenplaySequence, sequenceToEditorBlocks, editorBlocksToSequence, rebuildCharactersFromSequence } from '@/utils/screenplayEditor';
import { analyzeSceneInflections } from '@/utils/sceneDirectingApi';
import { directingDigest, DIRECTING_REVISION } from '../../supabase/functions/_shared/scene-directing';
import { genericAnalysisEstimate, analyzedPageCount, conversionAnalysisRun } from '@/utils/conversionProgress';
import { applyDirectedEntries } from '@/utils/sceneDirecting';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { assessVoiceAssignments, duplicateVoiceAssignments } from "@/utils/voiceAssignments";
import { loadStoredVoiceCatalog } from "@/utils/storedVoiceCatalog";
import { castingEstimate, castingPlan, castingSentenceBlocks, emptyCastingState, nextCastingState, restoreCastingState,
  withCastingBaselines, withCharacterAnnouncements, type CastingState } from '@/utils/castingAudio';
import { playbackManifest } from '@/utils/playbackManifest';
import { audioPreparationRequest } from '@/hooks/useServerAudioPreparation';
import { canonicalJson } from '@/utils/cachedPlayback';
import { SPEECH_LANGUAGES, SPEECH_LANGUAGE_STORAGE_KEY, restoreSpeechLanguage, type SpeechLanguage } from '@/utils/speechLanguage';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { TriangleAlert } from "lucide-react";
import { useAuth } from "@/auth/useAuth";
import {useLocation} from 'react-router-dom';
import RouteHeader from '@/components/RouteHeader';
import type { Character } from "@/types/screenplay";
import { useScreenplaySave } from "@/hooks/useScreenplaySave";
import ScreenplaySaveStatus from "@/components/ScreenplaySaveStatus";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from '@/integrations/supabase/types';
import { toast } from "sonner";
import { lastScreenplay, rememberScreenplay } from '@/utils/lastScreenplay';
import {
  restoreAssignments,
  restoreCharacters,
  restoreParseWarnings,
} from "@/utils/workflowRecovery";
import { useTtsAvailability } from "@/hooks/useTtsAvailability";
import {
  parseScreenplayTextSize,
  type ScreenplayTextSize,
} from "@/utils/screenplayTextSize";
import {
  parseScreenplayBackground,
  SCREENPLAY_BACKGROUND_STORAGE_KEY,
  SCREENPLAY_SPELLCHECK_STORAGE_KEY,
  parseScreenplaySpellcheck,
  type ScreenplayBackground,
} from "@/utils/screenplayAppearance";

const PREVIEW_VOICE_STORAGE_KEY = "tableread.previewVoiceId";
const SCREENPLAY_TEXT_SIZE_STORAGE_KEY = "tableread.screenplayTextSize";
type ConversionSizing=BlockSettings&{strategy:'sequential'|'cached'};

function conversionAudioPlan(characters:Character[],assignments:Record<string,string>,previous:CastingState,
  language:SpeechLanguage,sizing:ConversionSizing,sayNames:boolean,pageLimit:number,revisions:Map<string,string>){
  const {state,changed}=nextCastingState(characters,assignments,previous);state.page_limit=pageLimit;
  const scoped=withinConversionPages(castingSentenceBlocks(characters,state.assignments,language,sizing),sequenceToEditorBlocks(screenplaySequence(characters)),pageLimit);
  const sentences=withCastingBaselines(withCharacterAnnouncements(groupCachedAudioBlocks(scoped,{...sizing,mergeAcrossBlocks:!sayNames,strategy:sizing.strategy}),
    sayNames,state.assignments['SCENE NARRATOR']),state.baseline_voices);
  return {state,changed,plan:castingPlan(sentences,revisions)};
}

const ScreenplayWorkspace = ({ user }: { user: User }) => {
  const location=useLocation();
  const navigationRequest=useRef<{key:string;page:PublicPage}|null>(null);
  const requested=location.state?.publicPage;
  const handledNavigation=useRef<string|null>(null);
  const restoredNavigationReady=useRef(false);
  if(handledNavigation.current!==location.key&&['editor','scripts','upload','voices','settings'].includes(requested))navigationRequest.current={key:location.key,page:requested};
  // Support-service health is diagnostic only. Actual audio/retry results own
  // user-facing availability; a single failed probe must not block playback.
  useTtsAvailability();
  const [activePage, setActivePage] = useState<PublicPage>("editor");
  const [currentStep, setCurrentStep] = useState(1);
  const [characters, setCharacters] = useState<Character[]>([]);
  const [voiceAssignments, setVoiceAssignments] = useState<Record<string, string>>({});
  const [castingState, setCastingState] = useState<CastingState>(emptyCastingState);
  const [sayCharacterName, setSayCharacterName] = useState(false);
  const [audioQuote, setAudioQuote] = useState<{
    epoch: number; state: CastingState; previous: CastingState; changed: string[];
    plan: ReturnType<typeof castingPlan>; estimate: ReturnType<typeof castingEstimate>;
    revisions: Map<string, string>; characters: Character[]; screenplayId: string;
    sharedVoices: { voiceId: string; name: string; roles: string[] }[];
    inflectionsAdded: boolean;
    analysisComplete: boolean; analysisRun?: string; authorizedMinutes: number; sizing:ConversionSizing;
    setupEdits: boolean; savedAt: string | null;
  } | null>(null);
  const [confirmingCasting, setConfirmingCasting] = useState(false);
  const [analysisProgress,setAnalysisProgress]=useState<{completed:number;total:number;status:string}|null>(null);
  const [conversionInEditor,setConversionInEditor]=useState(false);
  const conversionInEditorRef=useRef(false);
  const showConversionInEditor=(value:boolean)=>{conversionInEditorRef.current=value;setConversionInEditor(value);};
  const [confirmationError,setConfirmationError]=useState('');
  const quoteTitleRef=useRef<HTMLHeadingElement>(null);
  const [pageChoice, setPageChoice] = useState<string | null>(null);
  const [addConversionInflections, setAddConversionInflections] = useState(false);
  const [changeConversionNarrator, setChangeConversionNarrator] = useState(false);
  const [conversionNarratorId, setConversionNarratorId] = useState('');
  const [conversionVoices, setConversionVoices] = useState<{id:string;name:string;display_name:string|null}[]>([]);
  const [conversionVoiceError, setConversionVoiceError] = useState('');
  const [conversionSetupStatus, setConversionSetupStatus] = useState('');
  const [conversionReviewError, setConversionReviewError] = useState('');
  const conversionReviewErrorRef = useRef<HTMLDivElement>(null);
  useEffect(()=>{if(conversionReviewError)conversionReviewErrorRef.current?.focus();},[conversionReviewError]);
  const reviewFlight = useRef(false);
  const reviewController = useRef<AbortController | null>(null);
  useEffect(() => () => reviewController.current?.abort(), []);
  const pageChoiceOpen = pageChoice !== null;
  useEffect(() => {
    if (!pageChoiceOpen) return;
    let cancelled=false;setConversionVoiceError('');setConversionVoices([]);
    loadStoredVoiceCatalog<{id:string;name:string;display_name:string|null}>(supabase,'id,name,display_name')
      .then(voices=>{if(!cancelled)setConversionVoices(voices);})
      .catch(()=>{if(!cancelled)setConversionVoiceError('Unable to load narrator voices. Please close and reopen this dialog to retry.');});
    return ()=>{cancelled=true;};
  },[pageChoiceOpen]);
  const estimatedPages = estimatedScreenplayPages(sequenceToEditorBlocks(screenplaySequence(characters))).total;
  const pageChoiceValid = pageChoice !== null && /^\d+$/.test(pageChoice) && Number(pageChoice) >= 1 && Number(pageChoice) <= estimatedPages;
  const castingFlight = useRef(false);
  const [, setGeneratedAudio] = useState<string | null>(null);
  const [screenplayId, setScreenplayId] = useState<string | null>(null);
  const [screenplayFilename, setScreenplayFilename] = useState<string | null>(null);
  const [scriptLanguage, setScriptLanguage] = useState<SpeechLanguage>('auto');
  const [savingScriptLanguage, setSavingScriptLanguage] = useState(false);
  const [pendingScriptLanguage, setPendingScriptLanguage] = useState<{id:string;language:SpeechLanguage}|null>(null);
  const currentScreenplayRef = useRef(screenplayId);
  currentScreenplayRef.current = screenplayId;
  const [defaultSpeechLanguage, setDefaultSpeechLanguage] = useState<SpeechLanguage>(() => {
    try { return restoreSpeechLanguage(window.localStorage.getItem(`${SPEECH_LANGUAGE_STORAGE_KEY}.${user.id}`) ?? 'en'); }
    catch { return 'en'; }
  });
  const handleDefaultSpeechLanguageChange = (language: SpeechLanguage) => {
    setDefaultSpeechLanguage(language);
    try { window.localStorage.setItem(`${SPEECH_LANGUAGE_STORAGE_KEY}.${user.id}`, language); }
    catch { toast.error('This browser could not save the language preference.'); }
  };
  const handleScriptLanguageChange = async (language: SpeechLanguage) => {
    if (language === scriptLanguage || !screenplayId || savingScriptLanguage) return;
    if (pendingScriptLanguage?.id !== screenplayId) { setPendingScriptLanguage(null); return; }
    const id = screenplayId; setSavingScriptLanguage(true);
    try {
      if (!(await persistCurrentScreenplay())) return;
      let update = supabase.from('screenplays').update({ speech_language: language === 'auto' ? null : language }).eq('id', id).eq('user_id', user.id);
      update = scriptLanguage === 'auto' ? update.is('speech_language', null) : update.eq('speech_language', scriptLanguage);
      const {data,error} = await update.select('id').single();
      if (error || data?.id !== id) throw new Error('Unable to save the language. Reopen the screenplay to check for changes in another tab.');
      if (currentScreenplayRef.current === id) setScriptLanguage(language);
      toast.success('Screenplay language saved.');
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Unable to save screenplay language.'); }
    finally { setSavingScriptLanguage(false); setPendingScriptLanguage(null); }
  };
  const [importNotes, setImportNotes] = useState<string[]>([]);
  const [isRestoringScreenplay, setIsRestoringScreenplay] = useState(false);
  const [checkingBaseline, setCheckingBaseline] = useState(false);
  const [missingVoiceRoles, setMissingVoiceRoles] = useState<string[] | null>(null);
  const baselineEpoch = useRef(0);
  useLayoutEffect(() => {
    baselineEpoch.current++; setAudioQuote(null); reviewController.current?.abort();
    showConversionInEditor(false);setAnalysisProgress(null);setConfirmationError('');
    if(!reviewFlight.current)setPageChoice(null);
  }, [characters, voiceAssignments, screenplayId, sayCharacterName, scriptLanguage]);
  useLayoutEffect(() => {
    // Switching from Voices to the editor is part of this confirmed workflow,
    // not an input change. Real source/casting changes still invalidate above.
    if(conversionInEditorRef.current)return;
    baselineEpoch.current++;setAudioQuote(null);reviewController.current?.abort();
    if(!reviewFlight.current)setPageChoice(null);
  }, [currentStep,activePage]);
  const [assignmentReadiness, setAssignmentReadiness] = useState({
    isComplete: false,
    remainingCharacters: [] as string[],
  });
  const [previewVoiceId, setPreviewVoiceId] = useState(() => {
    try {
      return window.localStorage.getItem(PREVIEW_VOICE_STORAGE_KEY) ?? "";
    } catch (error) {
      console.warn("[Index] Preview voice preference could not be restored:", error);
      return "";
    }
  });
  // Preserve the old script reading size; the replacement setting controls only the interface.
  const [screenplayTextSize] = useState<ScreenplayTextSize>(() => {
    try {
      return parseScreenplayTextSize(
        window.localStorage.getItem(SCREENPLAY_TEXT_SIZE_STORAGE_KEY),
      );
    } catch (error) {
      console.warn("[Index] Screenplay text-size preference could not be restored:", error);
      return parseScreenplayTextSize(null);
    }
  });

  const [screenplayBackground, setScreenplayBackground] = useState<ScreenplayBackground>(() => {
    try {
      return parseScreenplayBackground(window.localStorage.getItem(SCREENPLAY_BACKGROUND_STORAGE_KEY));
    } catch {
      return parseScreenplayBackground(null);
    }
  });
  const [screenplaySpellcheck, setScreenplaySpellcheck] = useState(() => {
    try { return parseScreenplaySpellcheck(window.localStorage.getItem(SCREENPLAY_SPELLCHECK_STORAGE_KEY)); }
    catch { return true; }
  });
  const handleScreenplaySpellcheckChange = useCallback((enabled: boolean) => {
    setScreenplaySpellcheck(enabled);
    try { window.localStorage.setItem(SCREENPLAY_SPELLCHECK_STORAGE_KEY, String(enabled)); }
    catch { toast.error("This browser could not save the spelling preference."); }
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const restoreLatestScreenplay = async () => {
      setIsRestoringScreenplay(true);

      try {
        const remembered=lastScreenplay(user.id);
        const owned=()=>supabase
          .from("screenplays")
          .select(
            "id,original_filename,parsed_characters,parse_warnings,character_assignments,voice_assignment_state,speech_language,created_at",
          )
          .eq("user_id", user.id);
        let result=remembered?await owned().eq('id',remembered).maybeSingle():null;
        if(result?.error)throw result.error; // Do not silently switch scripts on a network failure.
        if(!result?.data) {
          if(remembered)rememberScreenplay(user.id,null); // Removed or no longer owned.
          result=await owned().order('created_at',{ascending:false}).limit(1).maybeSingle();
        }
        const {data,error}=result;

        if (error) {
          throw error;
        }

        if (isCancelled) {
          return;
        }

        if (!data) {
          setCurrentStep(1);
          setActivePage("upload");
          return;
        }

        const restoredCharacters = restoreCharacters(data.parsed_characters);
        const restoredVoiceAssignments = restoreAssignments(
          data.character_assignments,
        );

        if (restoredCharacters.length === 0) {
          console.warn("[Index] Latest screenplay had no restorable characters:", {
            screenplayId: data.id,
            filename: data.original_filename,
          });
          setCurrentStep(1);
          setActivePage("upload");
          return;
        }

        setScreenplayId(data.id);
        setScreenplayFilename(data.original_filename);
        setCharacters(restoredCharacters);
        setVoiceAssignments(restoredVoiceAssignments);
        setCastingState(restoreCastingState(data.voice_assignment_state, restoredVoiceAssignments));
        setScriptLanguage(restoreSpeechLanguage(data.speech_language));
        setCurrentStep(2);
        setActivePage("editor");

        console.debug("[Index] Restored latest screenplay workflow:", {
          screenplayId: data.id,
          filename: data.original_filename,
          characterCount: restoredCharacters.length,
          assignmentCount: Object.keys(restoredVoiceAssignments).length,
        });
      } catch (error) {
        console.error("[Index] Failed to restore latest screenplay:", error);
        toast.error(
          error instanceof Error
            ? `Unable to restore your latest screenplay: ${error.message}`
            : "Unable to restore your latest screenplay.",
        );
      } finally {
        if (!isCancelled) {
          restoredNavigationReady.current=true;
          setIsRestoringScreenplay(false);
        }
      }
    };

    void restoreLatestScreenplay();

    return () => {
      isCancelled = true;
    };
  }, [user?.id]);

  useEffect(()=>{if(screenplayId)rememberScreenplay(user.id,screenplayId);},[user.id,screenplayId]);

  // Lives outside Activity: autosave continues while the editor is hidden.
  const { state: saveState, save, acceptSaved } = useScreenplaySave(
    user?.id, screenplayId, characters, voiceAssignments,
  );
  const persistCurrentScreenplay = useCallback(async () => {
    const saved = await save();
    if (!saved) toast.error("Your screenplay could not be saved. Retry before leaving this screenplay.");
    return saved;
  }, [save]);

  const handleFileProcessed = (processedScreenplay: ProcessedScreenplay) => {
    setScreenplayId(processedScreenplay.screenplayId);
    setScreenplayFilename(processedScreenplay.filename);
    setScriptLanguage(processedScreenplay.speechLanguage ?? defaultSpeechLanguage);
    setImportNotes(restoreParseWarnings(processedScreenplay.parseWarnings));
    setCharacters(processedScreenplay.characters);
    setVoiceAssignments({});
    setCastingState(emptyCastingState());
    setSayCharacterName(false);
    setAssignmentReadiness({ isComplete: false, remainingCharacters: [] });
    setCurrentStep(3);
    setActivePage("voices");
  };

  const handleVoiceAssignments = useCallback((assignments: Record<string, string>) => {
    setVoiceAssignments(assignments);
  }, []);

  const handleAssignmentReadiness = useCallback((
    isComplete: boolean,
    remainingCharacters: string[],
  ) => {
    setAssignmentReadiness((current) => {
      const unchanged =
        current.isComplete === isComplete &&
        current.remainingCharacters.length === remainingCharacters.length &&
        current.remainingCharacters.every(
          (character, index) => character === remainingCharacters[index],
        );

      return unchanged ? current : { isComplete, remainingCharacters };
    });
  }, []);

  const openHistoryItem = async (item: ScreenplayHistoryItem) => {
    if (item.id === screenplayId) {
      setCurrentStep(2);
      setActivePage("editor");
      return;
    }
    if (!(await persistCurrentScreenplay())) {
      return;
    }

    setIsRestoringScreenplay(true);

    try {
      const { data: screenplay, error: screenplayError } = await supabase
        .from("screenplays")
        .select(
          "id,original_filename,parsed_characters,parse_warnings,character_assignments,voice_assignment_state,speech_language",
        )
        .eq("id", item.id)
        .eq("user_id", user?.id ?? "")
        .single();

      if (screenplayError) {
        throw screenplayError;
      }

      const restoredCharacters = restoreCharacters(screenplay.parsed_characters);
      const restoredVoiceAssignments = restoreAssignments(
        screenplay.character_assignments,
      );

      if (restoredCharacters.length === 0) {
        toast.error("This screenplay has no restorable characters.");
        return;
      }

      setScreenplayId(screenplay.id);
      setScreenplayFilename(screenplay.original_filename);
      setCharacters(restoredCharacters);
      setVoiceAssignments(restoredVoiceAssignments);
      setCastingState(restoreCastingState(screenplay.voice_assignment_state, restoredVoiceAssignments));
      setScriptLanguage(restoreSpeechLanguage(screenplay.speech_language));
      setSayCharacterName(false);
      setGeneratedAudio(null);
      setAssignmentReadiness({ isComplete: false, remainingCharacters: [] });
      setCurrentStep(2);
      setActivePage("editor");

      console.debug("[Index] Opened fresh screenplay state from history:", {
        screenplayId: screenplay.id,
        filename: screenplay.original_filename,
        characterCount: restoredCharacters.length,
        assignmentCount: Object.keys(restoredVoiceAssignments).length,
      });
    } catch (error) {
      console.error("[Index] Failed to open screenplay from history:", {
        screenplayId: item.id,
        error,
      });
      toast.error(
        error instanceof Error
          ? `Unable to open screenplay: ${error.message}`
          : "Unable to open screenplay.",
      );
    } finally {
      setIsRestoringScreenplay(false);
    }
  };

  const handleNextStep = () => {
    if (checkingBaseline || castingFlight.current || currentStep !== 3 || !screenplayId) return;
    setAddConversionInflections(false);setChangeConversionNarrator(false);setConversionNarratorId('');setConversionSetupStatus('');setConversionReviewError('');
    setPageChoice(String(Math.min(castingState.page_limit ?? estimatedPages, estimatedPages)));
  };

  const reviewPageChoice = async () => {
    if (reviewFlight.current || checkingBaseline || castingFlight.current || currentStep !== 3 || !screenplayId) return;
    if (!pageChoiceValid) return;
    if(changeConversionNarrator&&!conversionNarratorId)return;
    reviewFlight.current=true;
    setConversionReviewError('');setConversionSetupStatus('Checking saved casting and available voices…');
    const pageLimit = Number(pageChoice);
    const epoch = baselineEpoch.current;
    let reviewCharacters=characters;
    const reviewAssignments=changeConversionNarrator?{...voiceAssignments,'SCENE NARRATOR':conversionNarratorId}:voiceAssignments;
    setCheckingBaseline(true);
    try {
      // Recheck the current catalog on click; a removed voice or stale readiness
      // callback must never allow a partially cast baseline to start.
      const voices = await loadStoredVoiceCatalog<{ id: string; updated_at: string; name: string; display_name: string | null }>(supabase, "id,updated_at,name,display_name");
      if (epoch !== baselineEpoch.current) throw new Error('Casting changed while preparing the estimate. Review your assignments and try again. No audio was submitted.');
      if(changeConversionNarrator&&!voices.some(v=>v.id===conversionNarratorId))throw new Error('The selected narrator voice is unavailable. Choose another voice before continuing.');
      const readiness = assessVoiceAssignments(characters, reviewAssignments, new Set(voices.map(voice => voice.id)));
      if (!readiness.isComplete) {
        setPageChoice(null);
        setMissingVoiceRoles([...readiness.missingCharacters, ...readiness.unavailableCharacters]);
        return;
      }
      if (!(await persistCurrentScreenplay())) throw new Error('Your screenplay could not be saved. Retry the save before reviewing audio. No audio was submitted.');
      if (epoch !== baselineEpoch.current) throw new Error('Casting changed while saving. Review your assignments and try again. No audio was submitted.');
      const { data: saved, error } = await supabase.from('screenplays').select('voice_assignment_state,parsed_characters,character_assignments,speech_language,updated_at')
        .eq('id', screenplayId).eq('user_id', user.id).single();
      if (error || !saved) throw new Error('Unable to check saved casting. No generation was started.');
      if (canonicalJson(saved.parsed_characters) !== canonicalJson(characters) || canonicalJson(saved.character_assignments) !== canonicalJson(voiceAssignments))
        throw new Error('The saved screenplay changed in another tab. Save and reopen it before reviewing casting.');
      if (restoreSpeechLanguage(saved.speech_language) !== scriptLanguage) throw new Error('The saved screenplay language changed in another tab. Reopen it before reviewing conversion.');
      const previous = restoreCastingState(saved.voice_assignment_state, voiceAssignments);
      const revisions = new Map(voices.map(v => [v.id, v.updated_at]));
      const router=await supabase.rpc('get_tts_router_settings' as never);
      if(router.error)throw new Error('Unable to load audio generation settings. Please try again.');
      const strategy=(router.data as {block_strategy?:string}|null)?.block_strategy==='sequential'?'sequential':'cached';
      const sizing:ConversionSizing={...blockSettings(router.data),strategy};
      const {state,changed,plan}=conversionAudioPlan(reviewCharacters,reviewAssignments,previous,scriptLanguage,sizing,sayCharacterName,pageLimit,revisions);
      setConversionSetupStatus('Checking saved audio and calculating the estimate…');
      const manifest = await playbackManifest(screenplayId, plan.clips, revisions);
      if (epoch !== baselineEpoch.current) throw new Error('Casting changed while calculating the estimate. Review your assignments and try again. No audio was submitted.');
      const sharedVoices = duplicateVoiceAssignments(reviewCharacters, reviewAssignments).map(group => {
        const voice = voices.find(voice => voice.id === group.voiceId);
        return { ...group, name: voice?.display_name?.trim() || voice?.name || 'Voice actor' };
      });
      const estimate=castingEstimate(plan.clips,manifest);
      setConfirmationError('');setAnalysisProgress(null);
      setAudioQuote({ epoch, state, previous, changed, plan, revisions, characters:reviewCharacters, screenplayId,
        sharedVoices, estimate, inflectionsAdded:addConversionInflections,analysisComplete:!addConversionInflections,sizing,
        authorizedMinutes:estimate.minutes+(addConversionInflections?castingEstimate(plan.clips,plan.clips.map(()=>({cached:false}))).minutes:0),
        setupEdits:canonicalJson(reviewCharacters)!==canonicalJson(characters)||canonicalJson(reviewAssignments)!==canonicalJson(voiceAssignments),savedAt:saved.updated_at??null });
      setPageChoice(null);
    } catch (error) {
      const message=reviewController.current?.signal.aborted
        ? 'Analysis stopped. No new audio was queued. An in-flight analysis may still finish and be billed.'
        : error instanceof Error ? error.message : 'Unable to check audio. No generation was started.';
      setConversionReviewError(message);
      if(reviewController.current?.signal.aborted)toast.info(message);else toast.error(message);
    } finally {
      reviewController.current=null;reviewFlight.current=false;setConversionSetupStatus('');
      setCheckingBaseline(false);
    }
  };

  const confirmCasting = async () => {
    if (!audioQuote || castingFlight.current || audioQuote.epoch !== baselineEpoch.current) return;
    let quote = audioQuote;
    let submittingAudio=false;
    let confirmationEpoch=quote.epoch;
    castingFlight.current = true; setConfirmingCasting(true);setConfirmationError('');
    try {
      if(quote.inflectionsAdded&&!quote.analysisComplete){
        // Confirmation, never Review, authorizes the directing pass. The saved
        // source/version and exact range are checked again before sending text.
        if(!(await persistCurrentScreenplay()))throw new Error('Save the screenplay before starting analysis.');
        const {data:saved,error}=await supabase.from('screenplays').select('updated_at').eq('id',quote.screenplayId).eq('user_id',user.id).single();
        if(error||!saved||saved.updated_at!==quote.savedAt||quote.epoch!==baselineEpoch.current)throw new Error('The screenplay changed. Review a fresh estimate before starting analysis.');
        const blocks=sequenceToEditorBlocks(screenplaySequence(quote.characters));
        const pages=quote.state.page_limit!;
        const starts=estimatedScreenplayPages(blocks).starts;
        const through=blocks.filter(b=>b.text.trim()&&(starts.get(b.id)??Infinity)<=pages).at(-1);
        if(!through)throw new Error('No spoken entries are available in the selected pages.');
        const run=quote.analysisRun??conversionAnalysisRun(user.id,quote.screenplayId,await directingDigest({revision:DIRECTING_REVISION,characters:quote.characters,pageLimit:pages}));
        quote={...quote,analysisRun:run};setAudioQuote(quote);
        const controller=new AbortController();reviewController.current=controller;
        flushSync(()=>{
          showConversionInEditor(true);setCurrentStep(2);setActivePage('editor');
          setAnalysisProgress({completed:0,total:pages,status:'Starting AI inflection analysis…'});
        });
        const result=await analyzeSceneInflections({screenplayId:quote.screenplayId,blocks,scope:'script',entryId:through.id,throughEntryId:through.id,
          conversionRunId:run,signal:controller.signal,onProgress:(done,total,lastId)=>setAnalysisProgress({completed:analyzedPageCount(blocks,pages,done,total,lastId),total:pages,status:'Analyzing scene context…'}),
          onWaiting:message=>setAnalysisProgress(p=>p?{...p,status:message}:p)});
        controller.signal.throwIfAborted();reviewController.current=null;
        if(quote.epoch!==baselineEpoch.current)throw new Error('The screenplay changed during analysis. No new directions or audio were submitted.');
        const directed=rebuildCharactersFromSequence(editorBlocksToSequence(applyDirectedEntries(blocks,result.entries,false)),quote.characters);
        const next=conversionAudioPlan(directed,quote.state.assignments,quote.previous,scriptLanguage,quote.sizing,sayCharacterName,pages,quote.revisions);
        quote={...quote,...next,characters:directed,analysisComplete:true,setupEdits:true};setAudioQuote(quote);
        setAnalysisProgress({completed:pages,total:pages,status:'Analysis complete. Preparing the confirmed audio plan…'});
      }
      // A disappeared cache object can increase the estimate. Require a fresh
      // confirmation rather than silently charging for additional audio.
      const manifest = await playbackManifest(quote.screenplayId, quote.plan.clips, quote.revisions);
      const fresh = castingEstimate(quote.plan.clips, manifest);
      if (quote.epoch !== baselineEpoch.current) throw new Error('Casting changed. Review the audio estimate again.');
      if (fresh.minutes > quote.authorizedMinutes || !quote.inflectionsAdded&&fresh.missing > quote.estimate.missing) {
        setAudioQuote({ ...quote, estimate: fresh,authorizedMinutes:fresh.minutes });
        showConversionInEditor(false);
        toast.info('Audio availability changed. Review the updated estimate before confirming.');
        return;
      }
      if(quote.setupEdits){
        if(!quote.savedAt)throw new Error('The saved screenplay version is unavailable. Reopen it before applying conversion options.');
        // Apply only after explicit confirmation, with a version guard. No text
        // or assignment snapshot goes into a query URL; a stale tab cannot win.
        const {data,error}=await supabase.from('screenplays').update({parsed_characters:quote.characters as unknown as Json,
          parsed_sequence:(quote.characters.find(c=>c.name==='SCREENPLAY_SEQUENCE')?.sequence??[]) as unknown as Json,character_assignments:quote.state.assignments})
          .eq('id',quote.screenplayId).eq('user_id',user.id).eq('updated_at',quote.savedAt).select('id').single();
        if(error||data?.id!==quote.screenplayId)throw new Error('The screenplay changed or the setup save was not acknowledged. No audio was submitted. Reopen the screenplay before continuing.');
        if(quote.epoch!==baselineEpoch.current)throw new Error('The current draft changed while saving setup. No audio was submitted. Reopen the screenplay to reconcile the saved options.');
        if(!acceptSaved(quote.characters,quote.state.assignments))throw new Error('A local save is pending. No audio was submitted. Reopen the screenplay to reconcile the saved options.');
        flushSync(()=>{setCharacters(quote.characters);setVoiceAssignments(quote.state.assignments);});
        confirmationEpoch=baselineEpoch.current;
        if(quote.inflectionsAdded){showConversionInEditor(true);setAnalysisProgress({completed:quote.state.page_limit!,total:quote.state.page_limit!,status:'Analysis complete. Submitting the confirmed audio plan…'});}
        setAudioQuote({...quote,epoch:confirmationEpoch,setupEdits:false});
      }
      submittingAudio=true;
      const job = await audioPreparationRequest({ action: 'submit-casting', screenplay_id: quote.screenplayId,
        expected_speech_language: scriptLanguage === 'auto' ? null : scriptLanguage,
        total: quote.plan.total, items: quote.plan.items, expected_casting_state: quote.previous,
        casting_state: quote.state, characters: quote.characters });
      if (!job) throw new Error('The server did not acknowledge the audio plan.');
      if (confirmationEpoch !== baselineEpoch.current) return;
      setCastingState(quote.state); setAudioQuote(null);
      showConversionInEditor(false);setAnalysisProgress(null);
      setCurrentStep(2); setActivePage('editor');
      toast.success('Voices confirmed. Saved clips are reused; any new audio is prepared in the background.');
    } catch (error) {
      const message=(reviewController.current?.signal.aborted?'Analysis paused. Completed batches are saved and will be reused when you continue. An in-flight request may still finish and incur charges.':error instanceof Error?error.message:'Unable to confirm casting.')+
        (submittingAudio?' If the connection was interrupted, the saved job may already be running; checking again will reuse it.':'');
      if(confirmationEpoch===baselineEpoch.current)setConfirmationError(message);
      toast.error(message);
    } finally { reviewController.current=null;castingFlight.current = false; setConfirmingCasting(false); }
  };

  const hasActiveScreenplay = Boolean(screenplayId && characters.length > 0);

  const handlePreviewVoiceChange = useCallback((voiceId: string) => {
    setPreviewVoiceId(voiceId);
    try {
      window.localStorage.setItem(PREVIEW_VOICE_STORAGE_KEY, voiceId);
      console.debug("[Index] Preview voice preference saved:", { voiceId });
    } catch (error) {
      console.warn("[Index] Preview voice preference could not be saved:", error);
    }
  }, []);

  const handleScreenplayBackgroundChange = useCallback((background: ScreenplayBackground) => {
    const value = parseScreenplayBackground(background);
    setScreenplayBackground(value);
    try {
      window.localStorage.setItem(SCREENPLAY_BACKGROUND_STORAGE_KEY, value);
    } catch (error) {
      console.warn("[Index] Screenplay background preference could not be saved:", error);
    }
  }, []);

  const handlePageChange = useCallback((page: PublicPage) => {
    if (page === "editor") {
      if (!hasActiveScreenplay) return;
      setCurrentStep(2);
    } else if (page === "upload") {
      setCurrentStep(1);
    } else if (page === "voices") {
      if (!hasActiveScreenplay) return;
      setCurrentStep(3);
    }

    setActivePage(page);
  }, [hasActiveScreenplay]);
  useEffect(()=>{
    const request=navigationRequest.current;
    if(!request||isRestoringScreenplay||!restoredNavigationReady.current)return;
    handledNavigation.current=request.key;navigationRequest.current=null;
    handlePageChange((request.page==='editor'||request.page==='voices')&&!hasActiveScreenplay?'scripts':request.page);
  },[location.key,isRestoringScreenplay,hasActiveScreenplay,handlePageChange]);

  return (
    <div className="min-h-screen bg-background">
      <Header
        user={user}
        activePage={activePage}
        hasActiveScreenplay={hasActiveScreenplay}
        onPageChange={handlePageChange}
        onBeforeLeave={persistCurrentScreenplay}
      />

      {hasActiveScreenplay && activePage !== "editor" && activePage !== 'settings' && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-2">
          <span className="text-sm">{screenplayFilename}</span>
          <ScreenplaySaveStatus state={saveState} onSave={persistCurrentScreenplay} />
        </div>
      )}
      {activePage==='settings'&&saveState.hasUnsavedChanges&&<div className="mx-auto max-w-6xl px-4 pt-3" role="status" aria-label="Screenplay save notice"><ScreenplaySaveStatus state={saveState} onSave={persistCurrentScreenplay}/></div>}
      {isRestoringScreenplay && (
        <main className="mx-auto max-w-6xl px-4 py-8">
          <p role="status">Opening screenplay…</p>
        </main>
      )}
      {!isRestoringScreenplay && activePage === "scripts" && (
        <main className="mx-auto max-w-6xl px-4 py-8">
          <ScreenplayHistory userId={user.id} activeScreenplayId={screenplayId} onOpen={openHistoryItem}
            onBeforeDelete={id => id === screenplayId ? persistCurrentScreenplay() : Promise.resolve(true)}
            onRenamed={(id, name) => { if (id === screenplayId) setScreenplayFilename(name); }}
            onDeleted={id => {
              if (id !== screenplayId) return;
              rememberScreenplay(user.id,null);
              setScreenplayId(null); setScreenplayFilename(null); setCharacters([]);
              setVoiceAssignments({}); setImportNotes([]); setGeneratedAudio(null);
              setAssignmentReadiness({ isComplete: false, remainingCharacters: [] });
              setCurrentStep(1);
            }} />
        </main>
      )}
      {!isRestoringScreenplay && activePage === "settings" && (
        <main className="mx-auto max-w-6xl px-4 py-8">
          <PublicSettings previewVoiceId={previewVoiceId} onPreviewVoiceChange={handlePreviewVoiceChange}
            sayCharacterName={sayCharacterName} onSayCharacterNameChange={setSayCharacterName}
            playbackPreferencesDisabled={conversionInEditor || confirmingCasting}
            screenplayBackground={screenplayBackground} onScreenplayBackgroundChange={handleScreenplayBackgroundChange}
            screenplaySpellcheck={screenplaySpellcheck} onScreenplaySpellcheckChange={handleScreenplaySpellcheckChange}
            defaultSpeechLanguage={defaultSpeechLanguage} onDefaultSpeechLanguageChange={handleDefaultSpeechLanguageChange}
            scriptLanguage={scriptLanguage} scriptFilename={screenplayFilename} onScriptLanguageChange={language => { if(screenplayId && language!==scriptLanguage)setPendingScriptLanguage({id:screenplayId,language}); }} scriptLanguageSaving={savingScriptLanguage} />
        </main>
      )}
      {screenplayId && (
        <Activity mode={!isRestoringScreenplay && activePage === "editor" && currentStep === 2 ? "visible" : "hidden"}>
          <main className="w-full">
            <ScreenplayEditor key={`${user.id}:${screenplayId}`} screenplayId={screenplayId}
              screenplayFilename={screenplayFilename} characters={characters} voiceAssignments={castingState.assignments}
              castingState={castingState} sayCharacterName={sayCharacterName}
              scriptLanguage={scriptLanguage}
              onCharactersChange={setCharacters} onSave={persistCurrentScreenplay} saveState={saveState}
              previewDisabledReason={conversionInEditor?'Inflection analysis must finish before new audio is prepared.':undefined} preferredPreviewVoiceId={previewVoiceId}
              conversionAnalysis={conversionInEditor?{completed:analysisProgress?.completed??0,total:analysisProgress?.total??audioQuote?.state.page_limit??1,
                status:analysisProgress?.status??'Preparing audio…',working:confirmingCasting,error:confirmationError,
                onPause:reviewController.current?()=>reviewController.current?.abort():undefined,onResume:()=>void confirmCasting(),
                onCancel:!audioQuote?.analysisComplete?()=>{setAudioQuote(null);showConversionInEditor(false);setAnalysisProgress(null);setConfirmationError('');}:undefined}:undefined}
              textSize={screenplayTextSize} background={screenplayBackground} spellCheck={screenplaySpellcheck} />
          </main>
        </Activity>
      )}
      {!isRestoringScreenplay && activePage !== "scripts" && activePage !== "settings" && currentStep !== 2 && (
      <main className={`mx-auto ${currentStep === 3 ? "max-w-[100rem] px-4 py-8" : "max-w-6xl px-4 py-8"}`}>

        <div className="space-y-8">
          {currentStep === 1 && (
            <FileUpload
              onFileProcessed={handleFileProcessed}
              defaultSpeechLanguage={defaultSpeechLanguage}
              userId={user.id}
              beforeUpload={persistCurrentScreenplay}
            />
          )}

          {currentStep === 3 && characters.length > 0 && (
            <CharacterVoiceAssignment
              onCommit={() => void handleNextStep()}
              commitLabel={Object.keys(castingState.assignments).length ? 'Update voices' : 'Assign voices'}
              committing={checkingBaseline || confirmingCasting}
              screenplayId={screenplayId}
              onBeforeAnalysis={persistCurrentScreenplay}
              characters={characters}
              initialAssignments={voiceAssignments}
              onVoiceAssignment={handleVoiceAssignments}
              onCharactersChange={setCharacters}
              onAssignmentReadinessChange={handleAssignmentReadiness}
            />
          )}

        </div>

      </main>
      )}
      <Dialog open={importNotes.length > 0} onOpenChange={open => { if (!open) setImportNotes([]); }}>
        <DialogContent className="max-h-[90svh] overflow-y-auto">
          <DialogHeader><DialogTitle>Screenplay parsing notes</DialogTitle>
            <DialogDescription>Review these items before generating audio. They do not prevent you from continuing.</DialogDescription></DialogHeader>
          <ul className="list-disc space-y-1 pl-5">{importNotes.map((note, index) => <li key={`${index}-${note}`}>{note}</li>)}</ul>
          <div className="flex justify-end"><Button onClick={() => setImportNotes([])}>Continue to Voices</Button></div>
        </DialogContent>
      </Dialog>
      <Dialog open={missingVoiceRoles !== null} onOpenChange={open => { if (!open) setMissingVoiceRoles(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign voices before continuing</DialogTitle>
            <DialogDescription>Choose an available voice for each role below. No baseline generation has started.</DialogDescription></DialogHeader>
          <ul className="max-h-80 list-disc overflow-y-auto pl-5">{missingVoiceRoles?.map(name => <li key={name}>{name}</li>)}</ul>
          <Button onClick={() => setMissingVoiceRoles(null)}>Return to casting</Button>
        </DialogContent>
      </Dialog>
      <Dialog open={pendingScriptLanguage !== null} onOpenChange={open => { if(!open&&!savingScriptLanguage)setPendingScriptLanguage(null); }}>
        <DialogContent onEscapeKeyDown={e=>{if(savingScriptLanguage)e.preventDefault();}} onPointerDownOutside={e=>{if(savingScriptLanguage)e.preventDefault();}}>
          <DialogHeader><DialogTitle>Change screenplay language?</DialogTitle>
            <DialogDescription>Changing the language can require regenerating the screenplay’s audio and consuming conversion credit. Existing files will not be deleted. No language change or new conversion is requested until you apply it.</DialogDescription></DialogHeader>
          <p>Requested language: {pendingScriptLanguage?.language==='auto'?'Automatic detection':pendingScriptLanguage&&SPEECH_LANGUAGES[pendingScriptLanguage.language]}</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={savingScriptLanguage} onClick={()=>setPendingScriptLanguage(null)}>Cancel language change</Button>
            <Button disabled={savingScriptLanguage} onClick={()=>{if(pendingScriptLanguage)void handleScriptLanguageChange(pendingScriptLanguage.language);}}>{savingScriptLanguage?'Saving language…':'Apply screenplay language'}</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={pageChoice !== null} onOpenChange={open => { if (!open && !checkingBaseline) setPageChoice(null); }}>
        <DialogContent className="max-h-[90svh] overflow-y-auto" onEscapeKeyDown={event => { if (checkingBaseline) event.preventDefault(); }} onPointerDownOutside={event => { if (checkingBaseline) event.preventDefault(); }}>
          <DialogHeader><DialogTitle>How many pages would you like to hear?</DialogTitle>
            <DialogDescription>Choose the first pages to convert to speech. You can increase this later from the Voices page.</DialogDescription></DialogHeader>
          <div className="space-y-3 text-sm">
            <Label htmlFor="conversion-pages">Pages to convert (1–{estimatedPages} estimated pages)</Label>
            <Input id="conversion-pages" type="number" inputMode="numeric" min={1} max={estimatedPages} step={1}
              value={pageChoice ?? ''} disabled={checkingBaseline} onChange={event => setPageChoice(event.target.value)} aria-describedby="conversion-pages-help" />
            <p id="conversion-pages-help" className="text-muted-foreground">Pages are estimated from screenplay line lengths, not the original PDF page numbers. Paragraphs starting in your range are included in full, so playback may extend beyond the last page. Your entire screenplay remains editable.</p>
            {!pageChoiceValid && <p role="alert">Enter a whole number from 1 to {estimatedPages}.</p>}
            <div className="space-y-2 rounded-lg border p-3">
              <label className="flex items-start gap-2"><input type="checkbox" checked={addConversionInflections} disabled={checkingBaseline}
                onChange={event=>setAddConversionInflections(event.target.checked)} />Add emotional inflection and delivery (AI)</label>
              <p className="text-xs text-muted-foreground">Send the relevant saved scene text to OpenAI to direct dialogue and narration in the selected pages. Full scene context is used; existing directions, pauses and unspoken text are preserved.</p>
              {addConversionInflections&&<p className="text-xs text-muted-foreground">Review only shows an estimate. AI analysis starts after you select Confirm and prepare audio. Existing directions are preserved. Analysis uses the shared daily allowance and can incur charges, including up to one replacement request per failed batch.</p>}
            </div>
            <div className="space-y-2 rounded-lg border p-3">
              <label className="flex items-start gap-2"><input type="checkbox" checked={changeConversionNarrator} disabled={checkingBaseline}
                onChange={event=>setChangeConversionNarrator(event.target.checked)} />Change scene narrator voice</label>
              {changeConversionNarrator&&<>
                <Label htmlFor="conversion-narrator">Scene narrator voice</Label>
                <select id="conversion-narrator" className="w-full rounded-md border bg-background p-2" value={conversionNarratorId} disabled={checkingBaseline||!conversionVoices.length}
                  onChange={event=>setConversionNarratorId(event.target.value)}>
                  <option value="">{conversionVoices.length?'Choose a narrator voice…':conversionVoiceError?'Voices unavailable':'Loading voices…'}</option>
                  {[...conversionVoices].sort((a,b)=>(a.display_name||a.name).localeCompare(b.display_name||b.name)).map(voice=><option key={voice.id} value={voice.id}>{voice.display_name?.trim()||voice.name}</option>)}
                </select>
                {conversionVoiceError&&<p role="alert" className="text-destructive">{conversionVoiceError}</p>}
                <p className="text-xs text-muted-foreground">Changes only SCENE NARRATOR for this screenplay. Character voices stay as assigned. A narrator change may require new audio.</p>
              </>}
            </div>
            {(addConversionInflections||changeConversionNarrator)&&<p className="text-xs text-muted-foreground">These options are included in the estimate but are not applied until you confirm. Cancel leaves your screenplay and narrator unchanged.</p>}
            {conversionSetupStatus&&<p role="status">{conversionSetupStatus}</p>}
            {conversionReviewError&&<div ref={conversionReviewErrorRef} tabIndex={-1} role="alert" className="space-y-2 rounded-md border border-destructive p-3">
              <p>{conversionReviewError}</p>
              <p>No new audio was submitted and no new inflections were applied. Nothing will be retried automatically.</p>
            </div>}
            <p>No new audio starts until you review the estimate and confirm. No introductory free-page allowance is applied yet.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={checkingBaseline} onClick={() => setPageChoice(null)}>Cancel</Button>
            {checkingBaseline&&reviewController.current&&<Button variant="outline" onClick={()=>reviewController.current?.abort()}>Stop analysis</Button>}
            <Button disabled={!pageChoiceValid || checkingBaseline || changeConversionNarrator&&(!conversionNarratorId||!!conversionVoiceError)} onClick={() => void reviewPageChoice()}>{checkingBaseline ? 'Preparing estimate…' : 'Review audio estimate'}</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={audioQuote !== null&&!conversionInEditor} onOpenChange={open => { if (!open && !confirmingCasting&&!conversionInEditorRef.current) setAudioQuote(null); }}>
        <DialogContent className="max-h-[90svh] overflow-y-auto" onOpenAutoFocus={event=>{event.preventDefault();quoteTitleRef.current?.focus();}} onEscapeKeyDown={event => { if (confirmingCasting) event.preventDefault(); }} onPointerDownOutside={event => { if (confirmingCasting) event.preventDefault(); }}>
          <DialogHeader><DialogTitle ref={quoteTitleRef} tabIndex={-1}>Confirm voice assignments</DialogTitle>
            <DialogDescription>Review the estimate, then confirm to start analysis and audio conversion. Cancel before confirmation leaves the new options unapplied.</DialogDescription></DialogHeader>
          {audioQuote && <div className="space-y-3 text-sm">
            <p>Conversion range: first {audioQuote.state.page_limit} estimated {audioQuote.state.page_limit === 1 ? 'page' : 'pages'} (complete paragraphs).</p>
            {audioQuote.inflectionsAdded&&<div className="space-y-2 rounded-md border p-3">
              <p>AI inflection analysis: approximately ${(genericAnalysisEstimate(audioQuote.state.page_limit!).dollars).toFixed(2)} and {Math.ceil(genericAnalysisEstimate(audioQuote.state.page_limit!).seconds/60)} minutes.</p>
              <p className="text-xs text-muted-foreground">Generic planning estimate: $0.01 and 10 seconds per estimated page, not script-specific analysis or a guaranteed price/time. Actual usage, provider delays and allowance resets vary. Up to one replacement per failed batch may be charged within the existing daily cap. No analysis runs until you confirm.</p>
              <p>Full scene context is used. Existing directions, pauses and unspoken text are preserved. Completed analysis is reused when resuming.</p>
            </div>}
            {confirmationError&&<p role="alert" className="rounded-md border border-destructive p-3">{confirmationError} No new audio is submitted until analysis completes. Completed batches will be reused.</p>}
            {audioQuote.sharedVoices.length > 0 && <Alert className="border-amber-500/50 bg-amber-500/10">
              <TriangleAlert className="h-4 w-4" />
              <AlertTitle>Shared voice assignments</AlertTitle>
              <AlertDescription>
                <ul className="my-2 list-disc pl-5">{audioQuote.sharedVoices.map(group =>
                  <li key={group.voiceId}><span className="font-medium">{group.name}</span>: {group.roles.join(', ')}</li>)}</ul>
                These roles will share the same voice. You can continue with these assignments, or cancel to change them.
              </AlertDescription>
            </Alert>}
            <p className="text-lg font-medium">Estimated new audio: {audioQuote.authorizedMinutes.toFixed(1)} minutes{audioQuote.inflectionsAdded&&!audioQuote.analysisComplete?' including an allowance for directed versions':''}</p>
            <p>{audioQuote.estimate.neutral} neutral clips · {audioQuote.estimate.modified} clips with saved intonation or delivery · {audioQuote.estimate.reused} saved clips reused.</p>
            <p className="text-muted-foreground">Based on spoken text, not the final generated duration. Actual minutes can vary. This includes both neutral and directed versions where needed, plus unfinished audio within your selected pages. Existing work already running may finish.</p>
            <p>{audioQuote.changed.length ? `Changed roles: ${audioQuote.changed.join(', ')}` : 'No voice assignments changed.'}</p>
            <p>Unchanged saved audio and your delivery settings are preserved.</p>
          </div>}
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={confirmingCasting} onClick={() => setAudioQuote(null)}>Cancel</Button>
            <Button disabled={confirmingCasting} onClick={() => void confirmCasting()}>{confirmingCasting ? analysisProgress?'Analyzing and preparing…':'Confirming…' : 'Confirm and prepare audio'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default function Index() {
  const { user, isLoading } = useAuth();
  if (isLoading) return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <p className="text-sm text-muted-foreground" role="status">Checking your TableRead.tv session…</p>
    </div>
  );
  // Retained editor state is private to this authenticated session.
  return user ? <ScreenplayWorkspace key={user.id} user={user} /> : <div className="min-h-screen bg-background"><RouteHeader/><Auth /></div>;
}
